import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

import { FontAwesomeSharedModule } from '../../../../shared/font-awesome/font-awesome';
import { RequirementHintComponent } from '../../../../shared/components/requirement-hint/requirement-hint.component';
import { SuggestOptionModalComponent } from '../../../../shared/components/suggest-option-modal/suggest-option-modal.component';
import {
  ICrossFieldRuleSpec,
  IMetadataFieldSchema,
  IMetadataSchema,
} from '../../../../shared/models/MetadataSchema';
import { MetadataSchemaService, SchemaCacheKey } from '../../../../shared/services/metadata-schema.service';
import { PendingOptionRequestsStore } from '../../../../shared/services/pending-option-requests.store';
import { ToastService } from '../../../../shared/services/toast.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { PostAnalysisService } from '../../services/post-analysis.service';
import { LabCustomTagService } from '../../services/lab-custom-tag.service';
import { IPostAnalysisPublication, KeyValueItem, MetadataTag } from '../../models/SequenceMetadata';
import { ILabCustomTag } from '../../models/LabCustomTag';
import { CreateCustomTagModalComponent } from './create-custom-tag-modal.component';

// Mirror the drawer's GPS validator so the post-analysis editor enforces the
// same client-side format hint before the server re-checks it.
const GPS_PATTERN = /^\d{1,2}\.\d{4,}\s+[NS],?\s*\d{1,3}\.\d{4,}\s+[EW]$/i;

interface ValidationIssue {
  severity: 'ERROR' | 'WARNING';
  code: string;
  message: string;
  template_keys: string[];
  issue_hash?: string;
}

interface PublishErrorBody {
  error?: string;
  code?: string;
  detail?: string;
  missing_metadata_keys?: string[];
  errors?: ValidationIssue[];
  warnings?: ValidationIssue[];
  unacknowledged_warning_hashes?: string[];
}

/**
 * Collapsible "Post-Analysis Metadata" section embedded in the lab sequence
 * drawer. Renders the dated published history (read-only with soft-remove for
 * editors) and a draft editor that reuses the drawer's typed metadata widgets,
 * requirement hints, suggest-option flow, and validation banner — scoped to
 * the file's POST_ANALYSIS schema and its open draft publication.
 *
 * Self-contained: it owns its own form + state and talks to
 * {@link PostAnalysisService} directly so the 1.5k-line drawer only needs to
 * embed `<app-post-analysis-section>` and pass the file id, source type, and
 * edit permission.
 */
@Component({
  selector: 'app-post-analysis-section',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    NgSelectModule,
    FontAwesomeSharedModule,
    RequirementHintComponent,
    SuggestOptionModalComponent,
    CreateCustomTagModalComponent,
  ],
  templateUrl: './post-analysis-section.component.html',
})
export class PostAnalysisSectionComponent implements OnChanges {
  /** PRIMARY file the publications attach to (the original, never a copy). */
  @Input({ required: true }) fileId!: number;
  /** Source type id for schema scoping; `null` => core-only schema. */
  @Input() sourceTypeId: number | null = null;
  /** Whether the surrounding drawer considers the file PRIMARY. */
  @Input() isPrimary = false;
  /**
   * When false (e.g. viewing a dataset copy), the draft editor and
   * soft-remove affordances are hidden and only the published history shows.
   * Publications still attach to / resolve from the original file.
   */
  @Input() editable = true;

  @ViewChild(SuggestOptionModalComponent) suggestModal?: SuggestOptionModalComponent;
  @ViewChild(CreateCustomTagModalComponent) createCustomTagModal?: CreateCustomTagModalComponent;

  private readonly fb = inject(FormBuilder);
  private readonly postAnalysisService = inject(PostAnalysisService);
  private readonly labCustomTagService = inject(LabCustomTagService);
  private readonly schemaService = inject(MetadataSchemaService);
  private readonly pendingStore = inject(PendingOptionRequestsStore);
  private readonly toast = inject(ToastService);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly expanded = signal(true);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly publishing = signal(false);
  readonly canEdit = signal(false);
  readonly publications = signal<IPostAnalysisPublication[]>([]);
  readonly draft = signal<IPostAnalysisPublication | null>(null);
  readonly schemaFields = signal<IMetadataFieldSchema[]>([]);
  readonly crossFieldRules = signal<ICrossFieldRuleSpec[]>([]);

  /** All custom tags defined for this file's lab (picker source). */
  readonly labCustomTags = signal<ILabCustomTag[]>([]);
  /** Custom tag ids currently added to the draft editor. */
  readonly selectedCustomTagIds = signal<number[]>([]);
  /** Picker model for adding an existing custom tag (reset after each add). */
  customPickerValue: ILabCustomTag | null = null;

  /**
   * Selected custom tags rendered as synthetic schema fields so the existing
   * typed widgets and form helpers (keyed by name) work unchanged. Custom tags
   * are never required and carry no source type.
   */
  readonly customFields = computed<IMetadataFieldSchema[]>(() => {
    const byId = new Map(this.labCustomTags().map((t) => [t.id, t]));
    return this.selectedCustomTagIds()
      .map((id) => byId.get(id))
      .filter((t): t is ILabCustomTag => !!t)
      .map((t) => this.toCustomField(t));
  });

  /** Existing custom tags not yet added to the draft (picker options). */
  readonly availableCustomTags = computed<ILabCustomTag[]>(() => {
    const selected = new Set(this.selectedCustomTagIds());
    return this.labCustomTags().filter((t) => !selected.has(t.id));
  });

  // Validation surface mirrors the promotion banner shape.
  readonly issues = signal<{
    errors: ValidationIssue[];
    warnings: ValidationIssue[];
    missingRequiredKeys: string[];
  } | null>(null);
  /** Warning hashes the user already acknowledged this session. */
  private acknowledgedWarnings = new Set<string>();
  /** Per-field server error message keyed by field name. */
  readonly serverErrors = signal<Record<string, string>>({});

  draftForm: FormGroup = this.fb.group({});

  /** Suggest-option modal binding state. */
  suggestTemplateId: number | null = null;
  suggestKeyName = '';

  readonly publishedPublications = computed(() =>
    this.publications()
      .filter((p) => p.status === 'PUBLISHED')
      .sort((a, b) => (b.published_at ?? '').localeCompare(a.published_at ?? ''))
  );

  /**
   * The current (most-recently published) set holds all active post-analysis
   * tags, since each new set is a full snapshot seeded from the prior one.
   */
  readonly currentPublication = computed(() => this.publishedPublications()[0] ?? null);
  /** Superseded sets, retained for audit and shown only on demand. */
  readonly previousPublications = computed(() => this.publishedPublications().slice(1));
  /** Whether the collapsed audit history of previous sets is expanded. */
  readonly showHistory = signal(false);

  toggleHistory(): void {
    this.showHistory.update((v) => !v);
  }

  readonly hasDraft = computed(() => this.draft() !== null);

  /** Show editor affordances only when both the server and the host allow it. */
  readonly showEditor = computed(() => this.canEdit() && this.editable);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileId'] && this.fileId != null) {
      this.resetState();
      if (this.isPrimary) {
        this.loadPublications();
        this.loadSchema();
        this.pendingStore.loadForFiles([this.fileId]);
      }
    }
  }

  private resetState(): void {
    this.publications.set([]);
    this.draft.set(null);
    this.issues.set(null);
    this.serverErrors.set({});
    this.acknowledgedWarnings = new Set();
    this.draftForm = this.fb.group({});
    this.labCustomTags.set([]);
    this.selectedCustomTagIds.set([]);
    this.customPickerValue = null;
  }

  toggle(): void {
    this.expanded.update((v) => !v);
  }

  // ---- loading ----------------------------------------------------------

  private loadPublications(): void {
    this.loading.set(true);
    this.postAnalysisService
      .getPublications(this.fileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.canEdit.set(res.can_edit);
          this.publications.set(res.publications ?? []);
          const draft = (res.publications ?? []).find((p) => p.status === 'DRAFT') ?? null;
          this.draft.set(draft);
          if (draft) this.buildForm(draft.tags);
          // Editors get the lab's custom-tag pick list; non-members never do
          // (keeps private custom-tag names off the read-only catalogue view).
          if (res.can_edit) this.loadCustomTags();
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.logger.warn('Failed to load post-analysis publications', 'PostAnalysisSectionComponent', err);
        },
      });
  }

  private loadSchema(): void {
    const key: SchemaCacheKey = this.sourceTypeId ?? 'core';
    this.schemaService
      .getSchema(key, 'POST_ANALYSIS')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (schema: IMetadataSchema) => {
          this.schemaFields.set([...(schema.templates ?? [])].sort((a, b) => a.sort_order - b.sort_order));
          this.crossFieldRules.set(schema.cross_field_rules ?? []);
          // Rebuild the form if a draft is already loaded so new controls exist.
          const draft = this.draft();
          if (draft) this.buildForm(draft.tags);
        },
        error: (err) => {
          this.logger.warn('Failed to load post-analysis schema', 'PostAnalysisSectionComponent', err);
        },
      });
  }

  private loadCustomTags(): void {
    this.labCustomTagService
      .listForFile(this.fileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.labCustomTags.set(res.custom_tags ?? []);
          // Rebuild now that custom-tag definitions are known so any custom
          // values already in the draft render with the right typed widget.
          const draft = this.draft();
          if (draft) this.buildForm(draft.tags);
        },
        error: (err) => {
          this.logger.warn('Failed to load lab custom tags', 'PostAnalysisSectionComponent', err);
        },
      });
  }

  // ---- draft lifecycle --------------------------------------------------

  openDraft(): void {
    this.saving.set(true);
    this.postAnalysisService
      .openDraft(this.fileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pub) => {
          this.saving.set(false);
          this.upsertPublication(pub);
          this.draft.set(pub);
          this.buildForm(pub.tags);
          this.expanded.set(true);
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.show(this.firstError(err) || 'Could not open a draft set.', 'error');
        },
      });
  }

  private buildForm(tags: MetadataTag[]): void {
    const byKey = new Map<string, MetadataTag>();
    for (const t of tags) byKey.set(t.key, t);

    // Auto-select any custom tags already present in this snapshot, unioned
    // with tags the user added but has not saved yet (so an empty added tag
    // survives a save round-trip rather than vanishing).
    const customByName = new Map(this.labCustomTags().map((t) => [t.name, t]));
    const idsFromTags = tags
      .filter((t) => t.is_custom || customByName.has(t.key))
      .map((t) => customByName.get(t.key)?.id)
      .filter((id): id is number => id != null);
    this.selectedCustomTagIds.set(Array.from(new Set([...this.selectedCustomTagIds(), ...idsFromTags])));

    const group: Record<string, AbstractControl> = {};
    for (const field of [...this.schemaFields(), ...this.customFields()]) {
      const existing = byKey.get(field.name);
      if (this.isMultiValue(field)) {
        const values = this.asArray(existing?.value);
        group[field.name] = this.fb.array(values.map((v) => this.fb.control(v)));
      } else {
        const validators = field.is_required ? [Validators.required] : [];
        if (field.data_type === 'GPS') validators.push(Validators.pattern(GPS_PATTERN));
        const value = existing ? (Array.isArray(existing.value) ? existing.value[0] : existing.value) : '';
        group[field.name] = this.fb.control(value ?? '', validators);
      }
    }
    this.draftForm = this.fb.group(group);
  }

  // ---- custom tags ------------------------------------------------------

  /** Build a synthetic schema field so a custom tag reuses the typed widgets. */
  private toCustomField(tag: ILabCustomTag): IMetadataFieldSchema {
    return {
      id: tag.id,
      name: tag.name,
      data_type: tag.data_type,
      is_pathogen_key: false,
      is_required: false,
      is_core: false,
      sort_order: 0,
      multiple_select: tag.multiple_select,
      source_type: null,
      source_type_name: null,
      options: (tag.options ?? []).map((v, i) => ({ name: v, value: v, sort_order: i })),
      rules: [],
    };
  }

  customTagFor(field: IMetadataFieldSchema): ILabCustomTag | undefined {
    return this.labCustomTags().find((t) => t.id === field.id);
  }

  isCustomPrivate(field: IMetadataFieldSchema): boolean {
    return !!this.customTagFor(field)?.is_private;
  }

  /** Add an existing custom tag to the draft from the picker. */
  addCustomTag(tag: ILabCustomTag | null | undefined): void {
    if (!tag || this.selectedCustomTagIds().includes(tag.id)) {
      this.customPickerValue = null;
      return;
    }
    this.selectedCustomTagIds.update((ids) => [...ids, tag.id]);
    this.ensureControlFor(this.toCustomField(tag));
    this.customPickerValue = null;
    this.draftForm.markAsDirty();
  }

  /** Remove a custom tag (and its value) from the draft editor. */
  removeCustomTag(field: IMetadataFieldSchema): void {
    this.selectedCustomTagIds.update((ids) => ids.filter((id) => id !== field.id));
    if (this.draftForm.get(field.name)) this.draftForm.removeControl(field.name);
    this.draftForm.markAsDirty();
  }

  private ensureControlFor(field: IMetadataFieldSchema): void {
    if (this.draftForm.get(field.name)) return;
    if (this.isMultiValue(field)) {
      this.draftForm.addControl(field.name, this.fb.array([]));
    } else {
      const validators = field.data_type === 'GPS' ? [Validators.pattern(GPS_PATTERN)] : [];
      this.draftForm.addControl(field.name, this.fb.control('', validators));
    }
  }

  openCreateCustomTag(): void {
    this.createCustomTagModal?.open();
  }

  onCustomTagCreated(tag: ILabCustomTag): void {
    this.labCustomTags.update((list) => [...list.filter((t) => t.id !== tag.id), tag]);
    this.addCustomTag(tag);
  }

  isMultiValue(field: IMetadataFieldSchema): boolean {
    return (
      field.data_type === 'LIST' ||
      field.data_type === 'MULTISELECT' ||
      (field.data_type === 'SELECT' && field.multiple_select)
    );
  }

  private asArray(value: string | string[] | undefined): string[] {
    if (Array.isArray(value)) return [...value];
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }

  // ---- typed-widget form helpers ---------------------------------------

  getControl(name: string): AbstractControl | null {
    return this.draftForm.get(name);
  }

  getArray(name: string): FormArray | null {
    const c = this.draftForm.get(name);
    return c instanceof FormArray ? c : null;
  }

  addListValue(name: string, input: HTMLInputElement): void {
    const value = input.value.trim();
    if (!value) return;
    const arr = this.getArray(name);
    if (!arr) return;
    if (!arr.controls.some((c) => c.value === value)) {
      arr.push(this.fb.control(value));
      arr.markAsDirty();
    }
    input.value = '';
  }

  removeListValue(name: string, index: number): void {
    const arr = this.getArray(name);
    if (arr) {
      arr.removeAt(index);
      arr.markAsDirty();
    }
  }

  onMultiSelectChange(values: string[], name: string): void {
    const arr = this.getArray(name);
    if (!arr) return;
    arr.clear();
    if (Array.isArray(values)) values.forEach((v) => arr.push(this.fb.control(v)));
    arr.markAsDirty();
  }

  removeMultiSelectValue(name: string, index: number): void {
    const arr = this.getArray(name);
    if (arr) {
      arr.removeAt(index);
      arr.markAsDirty();
    }
  }

  serverErrorFor(name: string): string | null {
    return this.serverErrors()[name] ?? null;
  }

  pendingValuesForField(field: IMetadataFieldSchema): string[] {
    return this.pendingStore.pendingValues(this.fileId, field.id);
  }

  // ---- save / remove / publish -----------------------------------------

  private collectTags(): KeyValueItem[] {
    const items: KeyValueItem[] = [];
    // Custom fields carry no source_type, so the existing emit logic naturally
    // sends them as plain { key, value } items.
    for (const field of [...this.schemaFields(), ...this.customFields()]) {
      const control = this.draftForm.get(field.name);
      if (!control) continue;
      const raw = control.value;
      let value: string | string[];
      if (this.isMultiValue(field)) {
        value = Array.isArray(raw) ? raw.filter((v) => v !== null && v !== '') : [];
        if ((value as string[]).length === 0) continue;
      } else {
        if (raw === null || raw === undefined || raw === '') continue;
        value = String(raw);
      }
      const item: KeyValueItem = { key: field.name, value };
      if (field.source_type != null) item.source_type = field.source_type;
      items.push(item);
    }
    return items;
  }

  saveDraft(): void {
    const draft = this.draft();
    if (!draft) return;
    this.clearServerErrors();
    this.saving.set(true);
    this.postAnalysisService
      .saveDraftTags(draft.id, this.collectTags())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pub) => {
          this.saving.set(false);
          this.upsertPublication(pub);
          this.draft.set(pub);
          this.buildForm(pub.tags);
          this.draftForm.markAsPristine();
          this.toast.show('Draft saved.', 'success');
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.show(this.firstError(err) || 'Failed to save draft.', 'error');
        },
      });
  }

  removeTag(publication: IPostAnalysisPublication, tag: MetadataTag): void {
    const ref: { metadata_tag_id?: number; key?: string; source_type?: number | null } = {
      source_type: tag.source_type,
    };
    if (tag.id != null) ref.metadata_tag_id = tag.id;
    else ref.key = tag.key;

    this.postAnalysisService
      .removeTag(publication.id, ref)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pub) => {
          this.upsertPublication(pub);
          if (pub.status === 'DRAFT') {
            this.draft.set(pub);
            this.buildForm(pub.tags);
          }
          this.toast.show('Tag removed.', 'success');
        },
        error: (err) => this.toast.show(this.firstError(err) || 'Failed to remove tag.', 'error'),
      });
  }

  publish(): void {
    const draft = this.draft();
    if (!draft) return;
    this.clearServerErrors();
    this.publishing.set(true);
    // Persist the current form before publishing so the server validates and
    // publishes exactly what the editor shows. Publishing without first saving
    // was why a filled-but-unsaved draft hit the "empty set" error.
    this.postAnalysisService
      .saveDraftTags(draft.id, this.collectTags())
      .pipe(
        switchMap((saved) => {
          this.upsertPublication(saved);
          this.draft.set(saved);
          return this.postAnalysisService.publish(draft.id, Array.from(this.acknowledgedWarnings));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.publishing.set(false);
          this.issues.set(null);
          this.upsertPublication(res.publication);
          this.draft.set(null);
          this.draftForm = this.fb.group({});
          this.toast.show(res.message || 'Post-analysis metadata published.', 'success');
        },
        error: (err) => {
          this.publishing.set(false);
          this.applyPublishOrSaveErrors(err?.error ?? {});
        },
      });
  }

  /** Re-publish acknowledging all current warnings (warnings-only block). */
  acknowledgeAndPublish(): void {
    const warnings = this.issues()?.warnings ?? [];
    for (const w of warnings) if (w.issue_hash) this.acknowledgedWarnings.add(w.issue_hash);
    this.publish();
  }

  hasOnlyWarnings(): boolean {
    const i = this.issues();
    return !!i && i.errors.length === 0 && i.missingRequiredKeys.length === 0 && i.warnings.length > 0;
  }

  /**
   * Route a failed publish. Because {@link publish} saves the draft first, the
   * failure can come from the save step (per-item tag errors shaped like
   * `{ key, value, error }` with a top-level `detail`) or the publish step
   * (validation issues carrying `template_keys`). Save-shaped errors are pinned
   * to their fields; everything else falls through to the validation banner.
   */
  private applyPublishOrSaveErrors(body: PublishErrorBody): void {
    const items = Array.isArray(body.errors) ? body.errors : [];
    const saveItems = items as unknown as { key?: string; value?: string; error?: string | string[] }[];
    const isSaveError = saveItems.some((e) => !!e && typeof e === 'object' && 'key' in e && !('template_keys' in e));
    if (isSaveError) {
      const serverErrors: Record<string, string> = {};
      for (const item of saveItems) {
        if (!item.key) continue;
        serverErrors[item.key] = Array.isArray(item.error)
          ? item.error.join(' ')
          : String(item.error ?? 'Invalid value.');
      }
      this.serverErrors.set(serverErrors);
      this.toast.show(body.detail || 'One or more tags were invalid.', 'error');
      return;
    }
    this.applyPublishErrors(body);
  }

  private applyPublishErrors(body: PublishErrorBody): void {
    const errors = Array.isArray(body.errors) ? body.errors : [];
    const warnings = Array.isArray(body.warnings) ? body.warnings : [];
    const missing = Array.isArray(body.missing_metadata_keys) ? body.missing_metadata_keys : [];

    if (errors.length === 0 && warnings.length === 0 && missing.length === 0) {
      this.issues.set(null);
      this.toast.show(body.error || body.detail || 'Publication blocked.', 'error');
      return;
    }

    this.issues.set({ errors, warnings, missingRequiredKeys: missing });

    const serverErrors: Record<string, string> = {};
    const attach = (issue: ValidationIssue) => {
      for (const key of issue.template_keys ?? []) {
        if (key) serverErrors[key] = issue.message;
      }
    };
    warnings.forEach(attach);
    errors.forEach(attach);
    for (const key of missing) serverErrors[key] = `${key} is required.`;
    this.serverErrors.set(serverErrors);
  }

  // ---- suggest option ---------------------------------------------------

  openSuggestOption(field: IMetadataFieldSchema): void {
    this.suggestTemplateId = field.id;
    this.suggestKeyName = field.name;
    setTimeout(() => this.suggestModal?.open(), 0);
  }

  onSuggestionSubmitted(): void {
    this.pendingStore.loadForFiles([this.fileId]);
  }

  // ---- formatting -------------------------------------------------------

  asString(value: string | string[]): string {
    return Array.isArray(value) ? (value[0] ?? '') : value;
  }

  displayValue(value: string | string[]): string {
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return value || '—';
  }

  formatDate(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  trackByField = (_: number, field: IMetadataFieldSchema) => field.id;
  trackByTagKey = (_: number, tag: MetadataTag) => tag.key;
  trackByPub = (_: number, pub: IPostAnalysisPublication) => pub.id;

  private upsertPublication(pub: IPostAnalysisPublication): void {
    this.publications.update((list) => {
      const next = list.filter((p) => p.id !== pub.id);
      next.push(pub);
      return next;
    });
  }

  private clearServerErrors(): void {
    this.serverErrors.set({});
    this.issues.set(null);
  }

  private firstError(err: unknown): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (err as any)?.error;
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (data.detail) return Array.isArray(data.detail) ? data.detail.join(' ') : String(data.detail);
    if (data.error) return String(data.error);
    if (data.file) return Array.isArray(data.file) ? data.file.join(' ') : String(data.file);
    return null;
  }
}
